import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from '@gatsbyjs/reach-router'
import PropTypes from 'prop-types'
import { Remarkable } from 'remarkable'
import { getConferenceLocation, setColorTheme } from 'theme/utils/generic-helpers'
import Countdown from 'src/components/countdown.js'
import { useAuthService } from 'theme/services/auth/auth-service'
import PurchaseTickets from 'src/components/modals/purchase-tickets/purchase-tickets'
import ApiGateway from 'theme/api/api-gateway'
import Subscribe from './subscribe'

const Tickets = ({
  allTickets,
  onDisplayTickets,
  conferenceOver,
  conference,
  hideDiscountedTickets,
  externalTickets,
}) => {
  const conferenceLocation = getConferenceLocation(conference)
  const location = useLocation()
  const md = new Remarkable({ breaks: true })
  const authService = useAuthService()
  const user = authService.user
  const [mainSaleTickets, setMainSaleTickets] = useState([])
  const [sampleTicket, setSampleTicket] = useState(null)
  const [countdownDate, setCountdownDate] = useState(null)
  const [upgradeSaleTickets, setUpgradeSaleTickets] = useState([])
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  const [selectedTickets, setSelectedTickets] = useState(null)
  const [discountTicket, setDiscountTicket] = useState({
    price: 99, //TODO: REPLACE HARD CODING
    currency: 'EUR',
  })
  const [maxDiscount, setMaxDiscount] = useState(0)

  const locale = conferenceLocation === 'usa' ? 'en-US' : 'en-GB'

  // rebuild tickets if the onSale tickets change
  useEffect(() => {
    async function getTicketDetails() {
      const inPersonCount = (await ApiGateway.getInPersonCount(conference.data.Conference_Id)) || 0
      let filteredTickets = onDisplayTickets
      // for each ticket in filtered tickets if the Delivery = "In-Person" then set teh Remaining_Tickets to the inPersonCount
      const remainingInPerson = conference.data.Physical_Capacity - inPersonCount
      filteredTickets.forEach((ticket) => {
        if (ticket.data.Delivery[0] === 'In-Person') {
          // Check if Remaining_Tickets is undefined or null (not set)
          if (ticket.data.Remaining_Tickets === undefined || ticket.data.Remaining_Tickets === null) {
            ticket.data.Remaining_Tickets = remainingInPerson
          } else if (ticket.data.Remaining_Tickets > remainingInPerson) {
            // Only update if the current value is greater than remainingInPerson
            ticket.data.Remaining_Tickets = remainingInPerson
          }
          // If Remaining_Tickets is 0, we leave it as 0 (sold out)
        } else if (ticket.data.Remaining_Tickets === undefined || ticket.data.Remaining_Tickets === null) {
          // For non-in-person tickets, only set default if not already set
          ticket.data.Remaining_Tickets = 1000
        }
        // If Remaining_Tickets is already set to 0 or any other value, we preserve it
      })
      setSampleTicket(filteredTickets[0])
      setMainSaleTickets(filteredTickets.filter((ticket) => !ticket.data.Is_Upgrade))
      setUpgradeSaleTickets(filteredTickets.filter((ticket) => ticket.data.Is_Upgrade))
      setMaxDiscount(
        filteredTickets.reduce((max, ticket) => Math.max(max, Number(ticket.data.Percent_Discount) || 0), 0),
      )

      // get the currency of a ticket
      let currency
      filteredTickets.forEach((ticket) => {
        if (!currency && ticket.data.Currency && ticket.data.Currency[0]) currency = ticket.data.Currency[0]
      })
      if (currency) {
        setDiscountTicket({
          price: 49,
          currency: currency,
        })
      }
    }
    getTicketDetails()
  }, [onDisplayTickets])

  useEffect(() => {
    if (sampleTicket) {
      setCountdownDate(
        new Date(sampleTicket.data.UTC_Start_Time).getTime() < Date.now()
          ? sampleTicket.data.UTC_End_Time
          : sampleTicket.data.UTC_Start_Time,
      )
    }
  }, [sampleTicket])

  const getLateTicketPrice = (ticket) => {
    const lateTicket = allTickets.nodes.filter((mainTicket) => {
      const sameType = mainTicket.data.Ticket_Type_Name === ticket.data.Ticket_Type_Name
      const notUpgrade = !mainTicket.data.Is_Upgrade
      if (sameType && notUpgrade) {
        // get the Ticket_Time_Period from the data.Price
        mainTicket.data.Prices.filter((price) => {
          const match = price.data.Ticket_Time_Period === 'Last Minute'
          if (match) {
            mainTicket.data.Late_Price = price.data.Price
            return true
          }
        })
      }
      return sameType && notUpgrade
    })
    return lateTicket[0] && lateTicket[0].data.Late_Price
  }

  const openPurchasePopup = (tickets, ticket) => () => {
    if (window) {
      // @ts-ignore: Unreachable code error
      window.dataLayer = window.dataLayer || []

      // clear the dataLayer
      window.dataLayer.push({ ecommerce: null })

      // @ts-ignore: Unreachable code error
      const selectedTicket = tickets.find((ticket1) => ticket1.id === ticket.id)
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          items: [
            {
              item_id: selectedTicket?.data?.Stripe_Price_Id,
              item_name: selectedTicket?.data?.Name,
              item_category: selectedTicket?.data?.Delivery[0],
              item_category2:
                conference.data.Conference_Id?.indexOf('community') !== -1 ? 'community' : 'conference',
              item_category3: conference.data.Conference_Id,
              item_category4: conference.data.Organisation,
              item_variant: selectedTicket?.data?.Access[0],
              price: selectedTicket?.data?.Price,
              quantity: 1,
            },
          ],
        },
      })
    }
    setSelectedTickets(tickets)
    setSelectedTicketId(ticket.id)
  }

  return (
    <div className="mt-4 pb-12 sm:mt-4 sm:pb-16 lg:mt-8 lg:pb-24">
      {/* // TODO - make this dynamic at run time */}
      {sampleTicket &&
        new Date(sampleTicket.data.UTC_Start_Time).getTime() < Date.now() &&
        new Date(sampleTicket.data.UTC_End_Time).getTime() - Date.now() < 1000 * 60 * 60 * 24 * 12 && (
          <div className="mb-6 bg-red-100 pb-6 pt-4 text-center text-2xl">
            {sampleTicket.data.Ticket_Time_Period} Sale ends in <br />
            <Countdown startTime={countdownDate} dark />
            {maxDiscount > 0 && <p className="text-md pt-4">Save up to {maxDiscount}%</p>}
          </div>
        )}
      {sampleTicket && new Date(sampleTicket.data.UTC_Start_Time).getTime() > Date.now() && (
        <div className="mb-6 bg-red-100 pb-6 pt-4 text-center text-2xl leading-relaxed">
          {sampleTicket.data.Ticket_Time_Period} takes place on <br />
          <span className="font-medium">
            {new Date(sampleTicket.data.UTC_Start_Time).toLocaleString(locale, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}{' '}
          </span>
          <br />
          <Countdown startTime={countdownDate} dark />
        </div>
      )}
      <div
        className={`mx-auto max-w-md space-y-4 lg:grid lg:max-w-7xl lg:justify-center ${
          mainSaleTickets.length > 1 ? 'md:grid-cols-2' : ''
        } ${
          mainSaleTickets.length === 2
            ? 'xl:grid-cols-2'
            : mainSaleTickets.length === 3
              ? 'xl:grid-cols-3'
              : mainSaleTickets.length === 4
                ? 'xl:grid-cols-4'
                : mainSaleTickets.length === 5
                  ? 'xl:grid-cols-5'
                  : ''
        } px-8 lg:gap-5 lg:space-y-0`}
      >
        {mainSaleTickets.map((ticket) => {
          let ticketDescription = md.render(ticket.data.Ticket_Includes)
          // replace every li with a styled version
          ticketDescription = ticketDescription.replace(/ul/, "ul class='space-y-4'")
          ticketDescription = ticketDescription.replace(
            /<li>/g,
            `<li class='flex space-x-3'><svg class="flex-shrink-0 h-5 w-5 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>`,
          )

          const onSale = new Date(ticket.data.UTC_Start_Time).getTime() < Date.now()
          return (
            <div
              key={ticket.id}
              className="flex max-w-xl flex-col overflow-hidden rounded-lg border border-gray-200 shadow-lg"
            >
              <div
                className={`top-0 p-4 text-center text-xl font-medium ${
                  ticket.data.Delivery[0] === 'Online'
                    ? 'bg-blue-200 text-blue-900'
                    : 'bg-green-200 text-green-900'
                }`}
              >
                <p className={'inline-flex rounded-lg text-lg font-medium'}>{ticket.data.Delivery}</p>
              </div>
              {ticket.data.Remaining_Tickets < 30 &&
                ticket.data.Remaining_Tickets > 0 &&
                ticket.data.Ticket_Type_Name?.indexOf('Executive') === -1 && (
                  <div className="bg-red-500 text-white py-1 text-center text-sm">
                    Only {ticket.data.Remaining_Tickets} tickets left
                  </div>
                )}
              {/* TODO: REMOVE THIS HARDCODING */}
              {/* {ticket.data.Remaining_Tickets > 30 &&
                conference.data.Conference_Id === 'usa-2024' &&
                ticket.data.Ticket_Type_Name?.indexOf('Workshop') === -1 &&
                ticket.data.Delivery.indexOf('Online') === -1 && (
                  <div className="bg-orange-500 text-white py-1 text-center text-sm">81% of tickets sold</div>
                )} */}
              <div className="bg-white px-4 sm:pb-10">
                <div className="relative">
                  <h3
                    className={`${
                      ticket.data.Remaining_Tickets < 20 && ticket.data.Remaining_Tickets > 0
                        ? 'h-16 pt-2'
                        : 'h-24 pt-8'
                    } pb-4`}
                  >
                    <p
                      className="text-4xl font-medium text-gray-900 md:text-2xl"
                      id={ticket.data.Ticket_Type_Name}
                    >
                      {ticket.data.Access}
                    </p>
                  </h3>
                  {ticket.data.Remaining_Tickets <= 0 && (
                    <div className="absolute top-0 left-10 p-2 transform rotate-12 bg-red-500 text-white font-bold text-2xl rounded-lg flex items-center justify-center">
                      <div className="">Sold Out</div>
                    </div>
                  )}
                </div>
                {/* <div className="mt-4 flex flex-wrap items-baseline text-4xl font-extrabold text-red-900">
                    Sold Out
                  </div> */}
                {ticket.data.Remaining_Tickets > 0 && (
                  <>
                    <div className="mt-4 flex flex-wrap items-baseline text-5xl font-extrabold">
                      {ticket.data.Price.toLocaleString(locale, {
                        style: 'currency',
                        currency: ticket.data.Currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                      {ticket.data.Price > 0 &&
                        ticket.data.Delivery[0] === 'In-Person' &&
                        !ticket.data.No_Tax[0] && (
                          <span className="ml-1 whitespace-nowrap text-base mt-1 font-normal text-gray-500">
                            (
                            {(
                              ticket.data.Price +
                              ticket.data.Price * (conference.data.Tax_Rate / 100)
                            ).toLocaleString(locale, {
                              style: 'currency',
                              currency: ticket.data.Currency,
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}{' '}
                            inc. Tax)
                          </span>
                        )}
                      {ticket.data.Price > 0 && ticket.data.Delivery[0] === 'Online' && (
                        <span className="ml-1 whitespace-nowrap text-base mt-1 font-normal text-gray-500">
                          (tax based on location)
                        </span>
                      )}
                      {ticket.data.Price === 0 && (
                        <span className="ml-3 text-3xl text-gray-500">
                          {' '}
                          - Free <span className="text-xl">&#127881;</span>
                        </span>
                      )}
                    </div>

                    <div className="mt-2 text-red-700">
                      Valid until:{' '}
                      {new Date(
                        new Date(ticket.data.UTC_End_Time).getTime() - 24 * 60 * 60 * 1000,
                      ).toLocaleString(locale, {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </div>
                    {getLateTicketPrice(ticket) && getLateTicketPrice(ticket) !== ticket.data.Price && (
                      <div className="mt-4 flex flex-wrap items-baseline rounded-md bg-slate-100 p-2 text-lg font-medium text-slate-700">
                        Standard:{' '}
                        {getLateTicketPrice(ticket).toLocaleString(locale, {
                          style: 'currency',
                          currency: ticket.data.Currency,
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </div>
                    )}
                  </>
                )}
                <p className="mt-5 text-lg text-gray-500">{ticket.data.Ticket_SubTitle}</p>
              </div>
              <div className="flex flex-1 flex-col justify-between space-y-6 bg-gray-50 px-4 pt-6 pb-8 sm:pt-6">
                <div dangerouslySetInnerHTML={{ __html: ticketDescription }} />
                <div className="w-full">
                  {ticket.data.Remaining_Tickets > 0 && onSale && (
                    <button
                      className="mt-8 inline-flex items-center rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                      onClick={openPurchasePopup(mainSaleTickets, ticket)}
                    >
                      {ticket.data.Sale_Button_Text}
                    </button>
                  )}
                  {ticket.data.Remaining_Tickets > 0 && !onSale && (
                    <div className="mt-8">
                      <button
                        onClick={() =>
                          document.getElementById(conferenceLocation + '-subscribe-email').focus()
                        }
                        className={
                          'w-full rounded-md border bg-gray-800 px-5 py-3 text-base font-medium text-white hover:bg-gray-700'
                        }
                      >
                        {ticket.data.Display_Button_Text}
                      </button>
                    </div>
                  )}

                  {ticket.data.Remaining_Tickets <= 0 && (
                    <div className="mt-8 inline-flex items-center rounded-md border border-transparent bg-gray-300 px-4 py-2 text-base font-medium text-gray-900 shadow-sm">
                      Sold Out
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {mainSaleTickets?.length === 1 &&
        mainSaleTickets?.[0]?.data?.Remaining_Tickets <= 0 &&
        conference.data.Type !== ' Conference' && (
          <div className="relative max-w-screen-xl mx-auto px-4 mt-8 py-8 rounded-lg sm:px-6 lg:px-8 lg:py-12 bg-rose-50">
            <div className="mx-auto md:w-3/5 md:pl-10 xl:pl-16">
              <div className="mb-4">
                <div
                  className="text-4xl leading-snug font-medium text-gray-900 text-center "
                  id="label-newsletter-option"
                >
                  <h2>Unfortunately you've missed this event</h2>
                  <p className="text-lg pt-2 pb-6">
                    But you can be the first to get notified about our next events
                  </p>
                </div>
              </div>
              <div className="mt-4 sm:max-w-md mx-auto">
                <Subscribe
                  id={`${conference.data.VenueCity}-tickets`}
                  theme="light"
                  options={[
                    {
                      name: 'newsletter',
                      title: 'I agree to receive emails from UXDX',
                    },
                    {
                      name: 'apac',
                      title: 'APAC Announcements & Special Offers',
                      default: true,
                    },
                    {
                      name: 'emea',
                      title: 'EMEA Announcements & Special Offers',
                      default: true,
                    },
                    {
                      name: 'usa',
                      title: 'USA Announcements & Special Offers',
                      default: true,
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        )}

      <div className="mx-auto grid max-w-7xl px-4 sm:px-6 md:grid-cols-2 md:gap-8 lg:mt-5 lg:px-8">
        {upgradeSaleTickets &&
          upgradeSaleTickets.map((ticket, index) => {
            return (
              <div key={'upgrade' + index} className="mt-4">
                <div className="mx-auto h-full max-w-md lg:max-w-5xl">
                  <div className="h-full rounded-lg border border-slate-200 bg-slate-100 px-6 py-8 shadow-lg sm:p-10 md:border-0 md:border-l-8 md:border-slate-600 lg:flex lg:items-center">
                    <div className="flex-1">
                      <div>
                        <h3 className="text-2xl font-medium text-gray-900" id="tier-hobby">
                          Upgrade to {ticket.data.Sale_Button_Text}
                        </h3>
                      </div>
                      <div className="mt-4 text-lg text-gray-600">
                        {ticket.data.Ticket_SubTitle}{' '}
                        <span className="font-semibold text-gray-900">
                          {' '}
                          {ticket.data.Price.toLocaleString(locale, {
                            style: 'currency',
                            currency: ticket.data.Currency,
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </span>
                        .
                      </div>
                    </div>
                    <div className="mt-6 rounded-md shadow lg:mt-0 lg:ml-10 lg:flex-shrink-0">
                      {ticket.data.Remaining_Tickets > 0 && (
                        <button
                          className="mt-8 inline-flex items-center rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                          onClick={openPurchasePopup([ticket], ticket)}
                        >
                          Upgrade Now
                        </button>
                      )}
                      {ticket.data.Remaining_Tickets <= 0 && (
                        <p className="flex items-center justify-center rounded-md border border-transparent bg-gray-300 px-5 py-3 text-base font-medium text-gray-900 ">
                          Sold Out
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
      </div>
      {!conferenceOver && !hideDiscountedTickets && (
        <div className="relative mx-auto mt-4 max-w-7xl px-4 sm:px-6 lg:mt-5 lg:px-8">
          <p className="pt-4 text-xl text-gray-500">
            Startup? Freelance? Student? Apply for discounted tickets{' '}
            <a
              href="https://airtable.com/appZDHX5d901bzueE/shrJjbvlNwjw8ymS0"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              here
            </a>
            .
          </p>
          {/* <div className='max-w-md mx-auto lg:max-w-5xl'>
            <div className='rounded-lg bg-gray-100 px-6 py-8 sm:p-10 lg:flex lg:items-center border border-gray-200 shadow-lg md:border-l-8 md:border-0 md:border-gray-600'>
              <div className='flex-1'>
                <div>
                  <h3 className='text-2xl font-medium text-gray-900' id='tier-hobby'>
                    Startups, Freelance and Students
                  </h3>
                </div>
                <div className='mt-4 text-lg text-gray-600'>Get a ticket to UXDX from just <span className='font-semibold text-gray-900'>{discountTicket.price.toLocaleString(locale, { style: 'currency', currency: discountTicket.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>. Checkout the eligibility criteria below.</div>
              </div>
              <div className='mt-6 rounded-md shadow lg:mt-0 lg:ml-10 lg:flex-shrink-0'>
                <a href='https://airtable.com/appZDHX5d901bzueE/shrJjbvlNwjw8ymS0' target='_blank' rel='noopener noreferrer' className='flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-gray-900 bg-white hover:bg-gray-50'>
                  Apply Now
                </a>
              </div>
            </div>
          </div> */}
        </div>
      )}
      {selectedTickets && (
        <PurchaseTickets
          isVisible={!!selectedTickets}
          conference={conference}
          allTickets={selectedTickets}
          selectedTicketId={selectedTicketId}
          onClose={() => {
            setSelectedTickets(null)
            setSelectedTicketId(null)
          }}
        />
      )}
    </div>
  )
}

Tickets.propTypes = {
  onDisplayTickets: PropTypes.arrayOf(
    PropTypes.shape({
      recordId: PropTypes.string.isRequired,
      data: PropTypes.shape({
        Is_Upgrade: PropTypes.bool,
        Ticket_SubTitle: PropTypes.string.isRequired,
        Ticket_Includes: PropTypes.string.isRequired,
        Currency: PropTypes.string.isRequired,
        Price: PropTypes.number.isRequired,
        Including_VAT: PropTypes.number, // calculate based on tax code
        VAT_Rate: PropTypes.number, // not really required
        Sold_Out: PropTypes.bool, // missing - would need to be calculated in ticket picker
        Remaining_Tickets: PropTypes.number,
        // Tito_Release: PropTypes.string.isRequired, // replace this with Stripe Price Id
        Stripe_Price_Id: PropTypes.string.isRequired,
        Hidden_Ticket: PropTypes.bool, // not required
        Sale_Button_Text: PropTypes.string.isRequired,
        Display_Button_Text: PropTypes.string,
        UTC_Start_Time: PropTypes.string.isRequired,
        UTC_End_Time: PropTypes.string.isRequired,
        Ticket_Time_Period: PropTypes.string.isRequired,
        Ticket_Type_Name: PropTypes.string.isRequired,
        Delivery: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        Access: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        Priority: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      }).isRequired,
    }).isRequired,
  ).isRequired,
  allTickets: PropTypes.object.isRequired,
  conferenceOver: PropTypes.bool,
  hideDiscountedTickets: PropTypes.bool,
}

export default Tickets
